import { useCallback, useMemo } from 'react'
import useSWR from 'swr'
import { useAxiosApi } from '@/hooks/useAxiosApi'

/**
 * Hook pour vérifier la disponibilité des composants à un emplacement
 * Utilise SWR pour la déduplication automatique des requêtes
 */
export function useComponentsAvailability({
    locationId,
    lines = [],
    isSimulation = false,
    debounceMs = 500,
}) {
    const { requestPost } = useAxiosApi()

    // Clé unique pour SWR basée sur les paramètres
    const swrKey = useMemo(() => {
        if (!lines || lines.length === 0) return null
        return ['components-availability', locationId, isSimulation, JSON.stringify(lines)]
    }, [locationId, isSimulation, lines])

    // Fetcher pour SWR
    const fetcher = useCallback(async () => {
        const response = await requestPost('/components/check-availability', {
            location_id: locationId,
            is_simulation: isSimulation,
            lines,
        })
        return response.data
    }, [requestPost, locationId, isSimulation, lines])

    const { data: result, error, isLoading: loading, isValidating, mutate } = useSWR(
        swrKey,
        fetcher,
        {
            dedupingInterval: debounceMs,
            revalidateOnFocus: false,
            keepPreviousData: true,
        }
    )

    // Helpers pour déterminer le statut
    const getComponentStatus = useCallback((component) => {
        const { available_quantity, required_quantity } = component

        if (available_quantity >= required_quantity) {
            return 'available'
        } else if (available_quantity > 0) {
            return 'partial'
        }
        return 'unavailable'
    }, [])

    // Variantes dont la nomenclature est inconnue : on ne peut rien garantir,
    // on les traite comme non disponibles plutôt que de les ignorer.
    const unmappedVariantIds = useMemo(
        () => result?.unmapped_variant_ids ?? [],
        [result]
    )

    const isVariantUnmapped = useCallback(
        (variantId) => unmappedVariantIds.includes(variantId),
        [unmappedVariantIds]
    )

    const allAvailable =
        unmappedVariantIds.length === 0 &&
        result?.components?.every((c) => getComponentStatus(c) === 'available')

    const hasUnavailable =
        unmappedVariantIds.length > 0 ||
        result?.components?.some((c) => getComponentStatus(c) === 'unavailable')

    // Récupérer les variant_ids impactés par un composant indisponible
    const getUnavailableVariantIds = useCallback(() => {
        if (!result?.components) return []

        const variantIds = new Set(unmappedVariantIds)
        result.components.forEach((component) => {
            if (getComponentStatus(component) !== 'available' && component.variant_ids) {
                component.variant_ids.forEach((id) => variantIds.add(id))
            }
        })
        return Array.from(variantIds)
    }, [result, getComponentStatus, unmappedVariantIds])

    // Vérifier si une variante spécifique a un problème
    const isVariantAvailable = useCallback((variantId) => {
        if (!result?.components) return true
        if (isVariantUnmapped(variantId)) return false

        return !result.components.some(
            (component) =>
                getComponentStatus(component) !== 'available' &&
                component.variant_ids?.includes(variantId)
        )
    }, [result, getComponentStatus, isVariantUnmapped])

    // Récupérer les composants indisponibles pour une variante
    const getUnavailableComponentsForVariant = useCallback((variantId) => {
        if (!result?.components) return []

        return result.components.filter(
            (component) =>
                getComponentStatus(component) !== 'available' &&
                component.variant_ids?.includes(variantId)
        )
    }, [result, getComponentStatus])

    // --- Helpers basés sur le stock physique (sans tenir compte des réservations) ---

    const getComponentPhysicalStatus = useCallback((component) => {
        const { stock_quantity, required_quantity } = component

        if (stock_quantity >= required_quantity) {
            return 'available'
        } else if (stock_quantity > 0) {
            return 'partial'
        }
        return 'unavailable'
    }, [])

    const allPhysicallyAvailable =
        unmappedVariantIds.length === 0 &&
        result?.components?.every(
            (c) => getComponentPhysicalStatus(c) === 'available'
        )

    const hasOutOfStock =
        unmappedVariantIds.length > 0 ||
        result?.components?.some(
            (c) => getComponentPhysicalStatus(c) === 'unavailable'
        )

    const getOutOfStockVariantIds = useCallback(() => {
        if (!result?.components) return []

        const variantIds = new Set(unmappedVariantIds)
        result.components.forEach((component) => {
            if (getComponentPhysicalStatus(component) !== 'available' && component.variant_ids) {
                component.variant_ids.forEach((id) => variantIds.add(id))
            }
        })
        return Array.from(variantIds)
    }, [result, getComponentPhysicalStatus, unmappedVariantIds])

    const hasVariantPhysicalStock = useCallback((variantId) => {
        if (!result?.components) return true
        if (isVariantUnmapped(variantId)) return false

        return !result.components.some(
            (component) =>
                getComponentPhysicalStatus(component) !== 'available' &&
                component.variant_ids?.includes(variantId)
        )
    }, [result, getComponentPhysicalStatus, isVariantUnmapped])

    const getOutOfStockComponentsForVariant = useCallback((variantId) => {
        if (!result?.components) return []

        return result.components.filter(
            (component) =>
                getComponentPhysicalStatus(component) !== 'available' &&
                component.variant_ids?.includes(variantId)
        )
    }, [result, getComponentPhysicalStatus])

    return {
        result,
        loading,
        isValidating,
        error: error ? 'Erreur lors de la vérification de disponibilité' : null,
        refresh: mutate,
        // Helpers
        getComponentStatus,
        allAvailable,
        hasUnavailable,
        getUnavailableVariantIds,
        isVariantAvailable,
        getUnavailableComponentsForVariant,
        unmappedVariantIds,
        isVariantUnmapped,
        // Helpers stock physique
        getComponentPhysicalStatus,
        allPhysicallyAvailable,
        hasOutOfStock,
        getOutOfStockVariantIds,
        hasVariantPhysicalStock,
        getOutOfStockComponentsForVariant,
    }
}
